import React, { Component, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  CircularProgress,
  Chip,
  Typography,
} from '@mui/material';
import PlayCircleIcon from '@mui/icons-material/PlayCircle';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useAuth } from '../../Contexts/AuthContext';
import { getVideos } from '../../Helpers/helpApi';
import FeedbackWidget from './FeedbackWidget';

// Error boundary for the iframe
class IframeErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() { return { hasError: true }; }
  render() {
    if (this.state.hasError) {
      return (
        <Alert severity="warning" sx={{ mt: 1 }}>
          Video could not be loaded.{' '}
          {this.props.onFallback && (
            <Typography
              component="span"
              variant="body2"
              color="primary"
              sx={{ cursor: 'pointer', textDecoration: 'underline' }}
              onClick={this.props.onFallback}
            >
              View written article instead
            </Typography>
          )}
        </Alert>
      );
    }
    return this.props.children;
  }
}

function formatDuration(seconds) {
  if (!seconds) return '';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

export default function VideoView() {
  const auth = useAuth();
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    setLoading(true);
    getVideos(auth.activeUser?.accessToken)
      .then((response) => {
        if (response?.data && Array.isArray(response.data)) {
          setVideos(response.data);
        } else {
          setVideos([]);
        }
      })
      .catch(() => setError('Failed to load videos.'))
      .finally(() => setLoading(false));
  }, [auth.activeUser?.accessToken]);

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>;
  if (error) return <Box sx={{ p: 2 }}><Alert severity="error">{error}</Alert></Box>;

  if (selected) {
    return (
      <Box sx={{ p: 2 }}>
        <Button startIcon={<ArrowBackIcon />} size="small" onClick={() => setSelected(null)} sx={{ mb: 1.5 }}>
          Back to videos
        </Button>
        <Typography variant="h6" gutterBottom>{selected.title}</Typography>
        <Chip label={selected.category} size="small" variant="outlined" sx={{ mb: 1.5 }} />
        <IframeErrorBoundary onFallback={() => setSelected(null)}>
          <Box
            component="iframe"
            src={selected.embed_url}
            title={selected.title}
            sandbox="allow-scripts allow-same-origin allow-presentation"
            allowFullScreen
            sx={{
              width: '100%',
              aspectRatio: '16/9',
              border: 'none',
              borderRadius: 1,
              display: 'block',
            }}
          />
        </IframeErrorBoundary>
        <FeedbackWidget contentId={selected.id} contentType="video" />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h6" gutterBottom>Video Tutorials</Typography>
      {videos.length === 0 ? (
        <Typography variant="body2" color="text.secondary">No videos available yet.</Typography>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {videos.map((video) => (
            <Card key={video.id} variant="outlined">
              <CardActionArea onClick={() => setSelected(video)}>
                <CardContent sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                  <PlayCircleIcon color="primary" sx={{ mt: 0.25, flexShrink: 0 }} />
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" fontWeight={500}>{video.title}</Typography>
                    <Box sx={{ display: 'flex', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
                      <Chip label={video.category} size="small" variant="outlined" />
                      {video.duration_seconds && (
                        <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>
                          {formatDuration(video.duration_seconds)}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </CardContent>
              </CardActionArea>
            </Card>
          ))}
        </Box>
      )}
    </Box>
  );
}
